import { Injectable } from '@angular/core';
import dayjs from 'dayjs/esm/index';

/**
 * Native port of the v1 `snapshot` service
 * (account-v1/src/components/common/date-range/snapshot.js) — the
 * sessionStorage-persisted date-range the dateRange directive family shares.
 * WS5 #10 Stage A inc 2r: replaces the `$injector.get('snapshot')` grab in the
 * five native consumers (big-picture, cooperative-big-picture, journeys,
 * audiences, admin-reports).
 *
 * Verbatim semantics: raw key `accountApp.snapshot` on window.sessionStorage
 * (v1's sessionStorage service is a plain JSON.stringify/parse wrapper, no
 * prefixing), stored start/end revived to Date on get, and the default =
 * 'Past 30 Days' from predefinedDateRanges.js ([now-30d, now-1d],
 * current-time-preserving — NOT midnight-floored), computed ONCE at service
 * instantiation exactly like the v1 lazy factory. The v1 dateRange directive
 * still WRITES the same key until its consumers die at Stage B — reading the
 * shared key (not a local default) is load-bearing for cross-page coherence.
 */
@Injectable({ providedIn: 'root' })
export class SnapshotService {
  private readonly defaultRange = {
    label: 'Past 30 Days',
    start: dayjs().subtract(30, 'days').toDate() as Date,
    end: dayjs().subtract(1, 'days').toDate() as Date,
  };

  get(): { label?: string; start: Date; end: Date } {
    const item = sessionStorage.getItem('accountApp.snapshot');
    const range = item ? JSON.parse(item) : null;
    if (range) {
      range.start = new Date(range.start);
      range.end = new Date(range.end);
      return range;
    }
    return this.defaultRange;
  }

  set(snapshot: { label?: string; start: Date; end: Date }): void {
    sessionStorage.setItem('accountApp.snapshot', JSON.stringify(snapshot));
  }
}
